import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  TablePagination,
} from "@mui/material";
import {
  FaWallet,
  FaPlus,
  FaMinus,
  FaSyncAlt,
  FaCopy,
  FaDollarSign,
} from "react-icons/fa";
import { MdAccountBalanceWallet } from "react-icons/md";
import { BiTransfer } from "react-icons/bi";
import DataGrid, { Column, Paging } from "devextreme-react/data-grid";
import Popup from "devextreme-react/popup";
import SelectBox from "devextreme-react/select-box";
import { useSelector, useDispatch } from "react-redux";
import { showNotification } from "../components/NotificationProvider";
import {
  fetchTraderBalance,
  fetchTraderDeposits,
  createTraderDeposit,
  createTraderWithdrawal,
  fetchTraderWithdrawals,
  fetchTraderHistory,
  clearTraderBalanceError,
} from "../store";
import "./TraderWalletView.css";
import aznGreenIcon from "../assets/images/azn-green.png";
import aznRedIcon from "../assets/images/azn-red.png";

const TraderWalletView = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const {
    balance = 0,
    traderName = "",
    walletAddress = "",
    walletNetwork = "",
    walletCurrency = "AZN",
    deposits = [],
    withdrawals = [],
    history = [],
    isLoading = false,
    error = null,
    depositSubmitting = false,
    lastUpdated = null,
    depositsPagination = { total: 0, page: 1, limit: 50, pages: 0 },
    withdrawalsPagination = { total: 0, page: 1, limit: 50, pages: 0 },
    historyPagination = { total: 0, page: 1, limit: 50, pages: 0 },
  } = useSelector((state) => state.traderBalance || {});

  const [depositPopupVisible, setDepositPopupVisible] = useState(false);
  const [withdrawalPopupVisible, setWithdrawalPopupVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [initialLoad, setInitialLoad] = useState(true);

  // Deposit form states
  const [depositAmount, setDepositAmount] = useState("");
  const [txId, setTxId] = useState("");

  // Withdrawal form states
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [withdrawalCurrency, setWithdrawalCurrency] = useState("USDT");
  const [withdrawalWalletAddress, setWithdrawalWalletAddress] = useState("");
  const [withdrawalNetwork, setWithdrawalNetwork] = useState("TRC20");

  useEffect(() => {
    if (user && user.user && user.user.role === "trader") {
      fetchWalletData();
    }
  }, [user]);

  useEffect(() => {
    if (error) {
      showNotification("Error", "error", error);
      dispatch(clearTraderBalanceError());
    }
  }, [error, dispatch]);

  const fetchWalletData = async () => {
    try {
      // Fetch balance and wallet info
      await dispatch(fetchTraderBalance()).unwrap();

      // Fetch deposits and withdrawals
      await dispatch(fetchTraderDeposits({ limit: 50, page: 1 })).unwrap();
      await dispatch(fetchTraderWithdrawals({ limit: 50, page: 1 })).unwrap();

      // After first successful load, set initialLoad to false
      setInitialLoad(false);
    } catch (error) {
      console.error("Error fetching wallet data:", error);
      showNotification("Error", "error", "Failed to load wallet data");
      setInitialLoad(false);
    }
  };

  const handleTabChange = async (event, newValue) => {
    setActiveTab(newValue);
    // Refetch only grid data when tab changes (not balance)
    if (newValue === 0) {
      // Wallet History - refetch deposits and withdrawals (await to prevent key field warning)
      try {
        await dispatch(
          fetchTraderDeposits({
            limit: depositsPagination.limit,
            page: depositsPagination.page,
          })
        ).unwrap();
        await dispatch(
          fetchTraderWithdrawals({
            limit: withdrawalsPagination.limit,
            page: withdrawalsPagination.page,
          })
        ).unwrap();
      } catch (error) {
        console.error("Error fetching deposits and withdrawals:", error);
      }
    } else if (newValue === 1) {
      // Transaction History - fetch transactions with pagination
      try {
        await dispatch(
          fetchTraderHistory({
            limit: historyPagination.limit,
            page: historyPagination.page,
          })
        ).unwrap();
      } catch (error) {
        console.error("Error fetching transaction history:", error);
      }
    }
  };

  // Pagination handlers for Wallet History (combined deposits/withdrawals)
  const handleWalletHistoryPageChange = async (event, newPage) => {
    const page = newPage + 1; // Material-UI uses 0-based, backend uses 1-based
    try {
      await Promise.all([
        dispatch(
          fetchTraderDeposits({
            limit: depositsPagination.limit,
            page,
          })
        ).unwrap(),
        dispatch(
          fetchTraderWithdrawals({
            limit: withdrawalsPagination.limit,
            page,
          })
        ).unwrap(),
      ]);
    } catch (error) {
      console.error("Error changing wallet history page:", error);
    }
  };

  const handleWalletHistoryRowsPerPageChange = async (event) => {
    const limit = parseInt(event.target.value, 10);
    try {
      await Promise.all([
        dispatch(fetchTraderDeposits({ limit, page: 1 })).unwrap(),
        dispatch(fetchTraderWithdrawals({ limit, page: 1 })).unwrap(),
      ]);
    } catch (error) {
      console.error("Error changing wallet history rows per page:", error);
    }
  };

  // Pagination handlers for Transaction History
  const handleTransactionHistoryPageChange = async (event, newPage) => {
    const page = newPage + 1; // Material-UI uses 0-based, backend uses 1-based
    try {
      await dispatch(
        fetchTraderHistory({
          limit: historyPagination.limit,
          page,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error changing transaction history page:", error);
    }
  };

  const handleTransactionHistoryRowsPerPageChange = async (event) => {
    const limit = parseInt(event.target.value, 10);
    try {
      await dispatch(fetchTraderHistory({ limit, page: 1 })).unwrap();
    } catch (error) {
      console.error("Error changing transaction history rows per page:", error);
    }
  };

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(walletAddress).then(() => {
      showNotification(
        "Success",
        "success",
        "Wallet address copied to clipboard!"
      );
    });
  };

  const handleDepositSubmit = async () => {
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      showNotification("Error", "error", "Please enter a valid amount");
      return;
    }

    try {
      await dispatch(
        createTraderDeposit({
          amount: parseFloat(depositAmount),
          txId: txId || undefined,
        })
      ).unwrap();

      showNotification(
        "Success",
        "success",
        "Deposit request submitted successfully!"
      );
      setDepositAmount("");
      setTxId("");
      setDepositPopupVisible(false);

      // Refresh deposit list (await to prevent key field warning)
      await dispatch(fetchTraderDeposits({ limit: 50, page: 1 })).unwrap();
    } catch (error) {
      console.error("Error submitting deposit:", error);
      showNotification(
        "Error",
        "error",
        error || "Failed to submit deposit request"
      );
    }
  };

  const handleWithdrawalSubmit = async () => {
    if (!withdrawalAmount || parseFloat(withdrawalAmount) <= 0) {
      showNotification("Error", "error", "Please enter a valid amount");
      return;
    }

    // Check if AZN amount exceeds balance
    if (withdrawalCurrency === "AZN") {
      const currentBalance = balance || 0;
      if (parseFloat(withdrawalAmount) > currentBalance) {
        showNotification(
          "Error",
          "error",
          `Insufficient balance. Your current balance is ${currentBalance.toFixed(
            2
          )} AZN`
        );
        return;
      }
    }

    if (!withdrawalWalletAddress || withdrawalWalletAddress.trim() === "") {
      showNotification("Error", "error", "Please enter a wallet address");
      return;
    }

    try {
      await dispatch(
        createTraderWithdrawal({
          amount: parseFloat(withdrawalAmount),
          currency: withdrawalCurrency,
          walletAddress: withdrawalWalletAddress.trim(),
          network: withdrawalNetwork,
        })
      ).unwrap();

      showNotification(
        "Success",
        "success",
        `Withdrawal request submitted successfully! Admin will review your ${withdrawalCurrency} withdrawal request.`
      );
      setWithdrawalAmount("");
      setWithdrawalWalletAddress("");
      setWithdrawalPopupVisible(false);

      // Refresh withdrawal list and transaction history
      await dispatch(fetchTraderWithdrawals({ limit: 50, page: 1 })).unwrap();
      await dispatch(fetchTraderHistory()).unwrap();
    } catch (error) {
      console.error("Error submitting withdrawal:", error);
      showNotification(
        "Error",
        "error",
        error || "Failed to submit withdrawal request"
      );
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "#4CAF50";
      case "pending":
        return "#FF9800";
      case "canceled":
        return "#757575";
      case "rejected":
        return "#F44336";
      default:
        return "#757575";
    }
  };

  const renderStatusCell = (cellData) => {
    const status = cellData.value;
    return (
      <Chip
        label={status.toUpperCase()}
        sx={{
          backgroundColor: getStatusColor(status),
          color: "white",
          fontWeight: "bold",
          fontSize: "1.1rem",
          height: "36px",
          padding: "8px 16px",
        }}
      />
    );
  };

  const renderAmountCell = (cellData) => {
    const row = cellData.data;
    const isWithdrawal = row.type === "withdrawal";
    const isCanceled = row.status === "canceled";

    if (isWithdrawal) {
      // Withdrawal display
      const originalCurrency = row.currency;
      const displayAmount = cellData.value;

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          {originalCurrency === "USDT" ? (
            <FaDollarSign
              size={18}
              color={isCanceled ? "#757575" : "#F44336"}
            />
          ) : (
            <img
              src={aznRedIcon}
              alt="AZN"
              style={{
                width: "18px",
                height: "18px",
                filter: isCanceled ? "grayscale(100%)" : "none",
                opacity: isCanceled ? 0.6 : 1,
              }}
            />
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              color: isCanceled ? "#757575" : "#F44336",
              fontSize: "1.4rem",
            }}
          >
            -{displayAmount.toFixed(2)} {originalCurrency}
          </Typography>
          {row.usdtAmount && row.aznAmount && (
            <Typography
              variant="body2"
              sx={{
                color: isCanceled ? "#757575" : "#FF9800",
                fontSize: "1.3rem",
                fontWeight: "500",
                marginLeft: "4px",
              }}
            >
              (
              {originalCurrency === "USDT"
                ? `${row.aznAmount.toFixed(2)} AZN`
                : `${row.usdtAmount.toFixed(2)} USDT`}
              )
            </Typography>
          )}
        </Box>
      );
    }

    // Deposit display
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <FaDollarSign size={18} color={isCanceled ? "#757575" : "#4CAF50"} />
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            color: isCanceled ? "#757575" : "#E0E6ED",
            fontSize: "1.4rem",
          }}
        >
          {cellData.value.toFixed(2)} USDT
        </Typography>
        {row.aznAmount && (
          <Typography
            variant="body2"
            sx={{
              color: isCanceled ? "#757575" : "#FF9800",
              fontSize: "1.3rem",
              fontWeight: "500",
              marginLeft: "4px",
            }}
          >
            ( {row.aznAmount.toFixed(2)} AZN )
          </Typography>
        )}
      </Box>
    );
  };

  const renderHistoryAmountCell = (cellData) => {
    const amount = cellData.value;
    const isPositive = amount >= 0;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
        }}
      >
        <img
          src={isPositive ? aznGreenIcon : aznRedIcon}
          alt="AZN"
          style={{ width: "20px", height: "20px" }}
        />
        <Typography
          variant="body2"
          sx={{
            fontWeight: "bold",
            color: isPositive ? "#4CAF50" : "#F44336",
            fontSize: "1.4rem",
          }}
        >
          {isPositive ? "+" : ""}
          {amount.toFixed(2)} AZN
        </Typography>
      </Box>
    );
  };

  const renderExchangeRateCell = (cellData) => {
    const rate = cellData.value;
    const row = cellData.data;
    const isCanceled = row.status === "canceled";

    if (!rate)
      return (
        <span
          style={{
            color: isCanceled ? "#757575" : "#B0BEC5",
            fontSize: "1.4rem",
          }}
        >
          -
        </span>
      );

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: "600",
            color: isCanceled ? "#757575" : "#E0E6ED",
            fontSize: "1.5rem",
          }}
        >
          {rate.toFixed(5)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: isCanceled ? "#757575" : "#B0BEC5", fontSize: "0.9rem" }}
        >
          ₼/USD
        </Typography>
      </Box>
    );
  };

  const renderHistoryTypeCell = (cellData) => {
    const type = cellData.value;

    // Map backend types to display labels and colors
    const typeConfig = {
      deposit: { label: "DEPOSIT", color: "#4CAF50" },
      withdrawal: { label: "WITHDRAWAL", color: "#F44336" },
      crypto_deposit: { label: "CRYPTO DEPOSIT", color: "#4CAF50" },
      crypto_withdrawal: { label: "CRYPTO WITHDRAWAL", color: "#F44336" },
    };

    const config = typeConfig[type] || {
      label: type?.toUpperCase() || "UNKNOWN",
      color: "#757575",
    };

    return (
      <Chip
        label={config.label}
        sx={{
          backgroundColor: config.color,
          color: "white",
          fontWeight: "bold",
          fontSize: "1.1rem",
          height: "36px",
          padding: "8px 16px",
        }}
      />
    );
  };

  const renderDepositPopup = () => (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#2E3B55",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          color: "#E0E6ED",
          mb: 3,
          textAlign: "center",
          fontWeight: "bold",
          fontSize: "2.5rem",
        }}
      >
        Add Funds to Wallet
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 3,
          flex: 1,
          alignItems: "stretch",
        }}
      >
        {/* Left Side - Wallet Address */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Card
            sx={{
              flex: 1,
              backgroundColor: "#3A4663",
              border: "2px solid #556CD6",
              borderRadius: "16px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ p: 3, flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: "#E0E6ED",
                  mb: 3,
                  fontSize: "1.8rem",
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                Deposit Address
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#B0BEC5",
                  mb: 2,
                  fontSize: "1.3rem",
                  textAlign: "center",
                }}
              >
                USDT - {walletNetwork}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#FF9800",
                  fontSize: "1.1rem",
                  textAlign: "center",
                  fontWeight: "500",
                }}
              >
                * Deposit USDT - Balance will be credited in AZN
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  mt: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4CAF50",
                    fontFamily: "monospace",
                    fontSize: "1.4rem",
                    wordBreak: "break-all",
                    lineHeight: 1.8,
                    textAlign: "center",
                    padding: "16px",
                    backgroundColor: "rgba(76, 175, 80, 0.1)",
                    borderRadius: "12px",
                    width: "100%",
                  }}
                >
                  {walletAddress}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleCopyAddress}
                  startIcon={<FaCopy size={20} />}
                  sx={{
                    color: "#556CD6",
                    borderColor: "#556CD6",
                    fontSize: "1.3rem",
                    fontWeight: "bold",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#4A5BC4",
                      backgroundColor: "rgba(85, 108, 214, 0.1)",
                    },
                  }}
                >
                  Copy Address
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Right Side - Form */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            justifyContent: "center",
          }}
        >
          {/* Amount Input */}
          <TextField
            label="Amount (USDT)"
            type="number"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            fullWidth
            required
            helperText="Your balance will be credited in AZN"
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "2rem",
                color: "#E0E6ED",
                padding: "20px 16px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "1.6rem",
                color: "#E0E6ED",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
              },
              "& .MuiFormHelperText-root": {
                fontSize: "1.2rem",
                color: "#FF9800",
                marginTop: "8px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaDollarSign color="#4CAF50" size={28} />
                </InputAdornment>
              ),
            }}
          />

          {/* Transaction ID Input */}
          <TextField
            label="TrxID (Optional)"
            value={txId}
            onChange={(e) => setTxId(e.target.value)}
            fullWidth
            placeholder="Enter transaction hash"
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "1.6rem",
                color: "#E0E6ED",
                padding: "18px 16px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "1.5rem",
                color: "#E0E6ED",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
              },
            }}
          />

          {/* Submit Button */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleDepositSubmit}
            disabled={depositSubmitting}
            sx={{
              py: 2.5,
              fontSize: "1.6rem",
              fontWeight: "bold",
              backgroundColor: "#556CD6",
              borderRadius: "14px",
              textTransform: "none",
              mt: 1,
              "&:hover": {
                backgroundColor: "#4A5BC4",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(85, 108, 214, 0.5)",
              },
              "&:disabled": {
                backgroundColor: "#666",
                color: "#999",
              },
              transition: "all 0.3s ease",
            }}
          >
            {depositSubmitting ? "Submitting..." : "Submit Deposit Request"}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  const renderWithdrawalPopup = () => (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#2E3B55",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          color: "#E0E6ED",
          mb: 4,
          textAlign: "center",
          fontWeight: "bold",
          fontSize: "2.5rem",
        }}
      >
        Request Withdrawal
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Currency Selection */}
        <Box>
          <Typography
            sx={{
              fontSize: "1.4rem",
              color: "#94a3b8",
              mb: 1,
            }}
          >
            Currency
          </Typography>
          <SelectBox
            items={["USDT", "AZN"]}
            value={withdrawalCurrency}
            onValueChanged={(e) => setWithdrawalCurrency(e.value)}
            placeholder="Select Currency"
            width="100%"
            stylingMode="outlined"
            style={{
              fontSize: "1.6rem",
            }}
          />
        </Box>

        {/* Amount Input */}
        <TextField
          label={`Amount (${withdrawalCurrency})`}
          type="number"
          value={withdrawalAmount}
          onChange={(e) => setWithdrawalAmount(e.target.value)}
          fullWidth
          required
          error={
            withdrawalCurrency === "AZN" &&
            withdrawalAmount &&
            parseFloat(withdrawalAmount) > (balance || 0)
          }
          sx={{
            "& .MuiInputBase-root": {
              backgroundColor: "#0f172a",
              borderRadius: "8px",
            },
            "& .MuiInputBase-input": {
              fontSize: "2rem",
              color: "#E0E6ED",
              fontWeight: "600",
              padding: "14px 16px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "1.4rem",
              color: "#94a3b8",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#475569",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {withdrawalCurrency === "USDT" ? (
                  <FaDollarSign color="#FF9800" size={24} />
                ) : (
                  <img
                    src={aznRedIcon}
                    alt="AZN"
                    style={{ width: "24px", height: "24px" }}
                  />
                )}
              </InputAdornment>
            ),
          }}
        />

        {/* Network */}
        <TextField
          label="Network"
          value={withdrawalNetwork}
          onChange={(e) => setWithdrawalNetwork(e.target.value)}
          fullWidth
          sx={{
            "& .MuiInputBase-root": {
              backgroundColor: "#0f172a",
              borderRadius: "8px",
            },
            "& .MuiInputBase-input": {
              fontSize: "1.6rem",
              color: "#E0E6ED",
              fontWeight: "600",
              padding: "14px 16px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "1.4rem",
              color: "#94a3b8",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#475569",
            },
          }}
        />

        {/* Wallet Address Input */}
        <TextField
          label="USDT Wallet Address"
          value={withdrawalWalletAddress}
          onChange={(e) => setWithdrawalWalletAddress(e.target.value)}
          fullWidth
          required
          placeholder="Enter your USDT wallet address"
          sx={{
            "& .MuiInputBase-root": {
              backgroundColor: "#0f172a",
              borderRadius: "8px",
            },
            "& .MuiInputBase-input": {
              fontSize: "1.6rem",
              color: "#E0E6ED",
              fontFamily: "monospace",
              fontWeight: "600",
              padding: "14px 16px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "1.4rem",
              color: "#94a3b8",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#475569",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaWallet color="#4CAF50" size={20} />
              </InputAdornment>
            ),
          }}
        />

        {/* Submit Button */}
        <Button
          variant="contained"
          fullWidth
          onClick={handleWithdrawalSubmit}
          disabled={depositSubmitting}
          sx={{
            py: 2.5,
            fontSize: "1.6rem",
            fontWeight: "bold",
            backgroundColor: "#FF9800",
            borderRadius: "14px",
            textTransform: "none",
            mt: 2,
            "&:hover": {
              backgroundColor: "#F57C00",
              transform: "translateY(-2px)",
              boxShadow: "0 8px 24px rgba(255, 152, 0, 0.5)",
            },
            "&:disabled": {
              backgroundColor: "#666",
              color: "#999",
            },
            transition: "all 0.3s ease",
          }}
        >
          {depositSubmitting ? "Submitting..." : "Submit Withdrawal Request"}
        </Button>
      </Box>
    </Box>
  );

  // Only show full page loading on initial load
  if (isLoading && initialLoad) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography variant="h6" sx={{ color: "#E0E6ED" }}>
          Loading wallet data...
        </Typography>
      </Box>
    );
  }

  return (
    <div className="trader-wallet-view">
      {/* Header */}
      <Box sx={{ mb: 3, flexShrink: 0 }}>
        <Typography variant="h4" sx={{ color: "#E0E6ED", mb: 1 }}>
          Welcome, {traderName}
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "#B0BEC5" }}>
          Manage your trading wallet and deposits
        </Typography>
      </Box>

      {/* Wallet Balance Card - Modern Design */}
      <Card
        sx={{
          mb: 3,
          flexShrink: 0,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          border: "none",
          borderRadius: "20px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
          overflow: "hidden",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "200px",
            height: "200px",
            background: "rgba(255,255,255,0.1)",
            borderRadius: "50%",
            transform: "translate(50%, -50%)",
          },
        }}
      >
        <CardContent sx={{ p: 4, position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: "16px",
                  padding: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MdAccountBalanceWallet size={56} color="#ffffff" />
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    mb: 1,
                    fontSize: "1.1rem",
                    fontWeight: 500,
                    letterSpacing: "0.5px",
                  }}
                >
                  Current Balance
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    color: "#ffffff",
                    fontWeight: "bold",
                    fontSize: "3.5rem",
                    textShadow: "0 2px 10px rgba(0,0,0,0.2)",
                  }}
                >
                  {balance.toFixed(2)}{" "}
                  <span style={{ fontSize: "2rem" }}>AZN</span>
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="contained"
                startIcon={<FaPlus size={20} />}
                onClick={() => setDepositPopupVisible(true)}
                sx={{
                  backgroundColor: "#4CAF50",
                  "&:hover": {
                    backgroundColor: "#45A049",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(76, 175, 80, 0.4)",
                  },
                  px: 4,
                  py: 2,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  borderRadius: "12px",
                  textTransform: "none",
                  transition: "all 0.3s ease",
                }}
              >
                ADD FUNDS
              </Button>
              <Button
                variant="contained"
                startIcon={<FaMinus size={20} />}
                onClick={() => setWithdrawalPopupVisible(true)}
                sx={{
                  backgroundColor: "#FF9800",
                  "&:hover": {
                    backgroundColor: "#F57C00",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(255, 152, 0, 0.4)",
                  },
                  px: 4,
                  py: 2,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  borderRadius: "12px",
                  textTransform: "none",
                  transition: "all 0.3s ease",
                }}
              >
                REQUEST WITHDRAWAL
              </Button>
              <IconButton
                onClick={fetchWalletData}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "white",
                  width: 56,
                  height: 56,
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.3)",
                    transform: "rotate(180deg)",
                  },
                  transition: "all 0.5s ease",
                }}
              >
                <FaSyncAlt size={24} />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Wallet & Transaction History */}
      <Card
        sx={{
          backgroundColor: "#2E3B55",
          width: "100%",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          borderRadius: "16px",
        }}
      >
        <CardContent
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            "&:last-child": { pb: 0 },
          }}
        >
          {/* Header with Tabs */}
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 2,
              flexShrink: 0,
              borderBottom: "2px solid #3A4663",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#556CD6",
                  height: "3px",
                },
                "& .MuiTab-root": {
                  color: "#B0BEC5",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                  textTransform: "none",
                  minHeight: "56px",
                  "&.Mui-selected": {
                    color: "#556CD6",
                  },
                  "& .MuiTab-iconWrapper": {
                    marginRight: "10px",
                  },
                },
              }}
            >
              <Tab
                icon={<FaWallet size={22} />}
                iconPosition="start"
                label="Wallet History"
              />
              <Tab
                icon={<BiTransfer size={22} />}
                iconPosition="start"
                label="Transaction History"
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ flex: 1, minHeight: 0, p: 3 }}>
            {activeTab === 0 && (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {!initialLoad && !isLoading && (
                  <>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                      <DataGrid
                        dataSource={[
                          ...deposits
                            .filter((d) => d && d._id)
                            .map((d) => ({ ...d, type: "deposit" })),
                          ...withdrawals
                            .filter((w) => w && w._id)
                            .map((w) => ({ ...w, type: "withdrawal" })),
                        ].sort(
                          (a, b) =>
                            new Date(b.createdAt) - new Date(a.createdAt)
                        )}
                        keyExpr="_id"
                        showBorders={true}
                        showRowLines={true}
                        showColumnLines={true}
                        rowAlternationEnabled={true}
                        className="trader-wallet-grid"
                        height="100%"
                        width="100%"
                        remoteOperations={false}
                        noDataText="No transaction history available"
                      >
                        <Paging enabled={false} />

                        <Column
                          dataField="type"
                          caption="Type"
                          cellRender={(cellData) => {
                            const row = cellData.row.data;
                            const isCanceled = row.status === "canceled";

                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 1,
                                }}
                              >
                                <Chip
                                  label={
                                    cellData.value === "deposit"
                                      ? "Deposit"
                                      : "Withdrawal"
                                  }
                                  size="small"
                                  sx={{
                                    backgroundColor: isCanceled
                                      ? "#757575"
                                      : cellData.value === "deposit"
                                      ? "#4CAF50"
                                      : "#F44336",
                                    color: "#fff",
                                    fontWeight: "bold",
                                    fontSize: "1rem",
                                    minWidth: "110px",
                                    px: 1,
                                  }}
                                />
                              </Box>
                            );
                          }}
                          minWidth={150}
                          alignment="center"
                        />
                        <Column
                          dataField="amount"
                          caption="Amount"
                          cellRender={renderAmountCell}
                          minWidth={320}
                          alignment="center"
                        />
                        <Column
                          dataField="status"
                          caption="Status"
                          cellRender={renderStatusCell}
                          minWidth={200}
                          alignment="center"
                        />
                        <Column
                          dataField="exchangeRate"
                          caption="Exchange Rate"
                          cellRender={renderExchangeRateCell}
                          minWidth={120}
                          alignment="center"
                        />
                        <Column
                          dataField="binanceTxId"
                          caption="Transaction ID"
                          minWidth={125}
                          alignment="center"
                          cellRender={(cellData) => {
                            const row = cellData.row.data;
                            const isCanceled = row.status === "canceled";
                            return (
                              <span
                                style={{
                                  color: isCanceled ? "#757575" : "#E0E6ED",
                                }}
                              >
                                {cellData.value || "-"}
                              </span>
                            );
                          }}
                        />
                        <Column
                          dataField="createdAt"
                          caption="Created"
                          dataType="datetime"
                          format="dd/MM/yyyy HH:mm"
                          minWidth={200}
                          alignment="center"
                          cssClass="date-column"
                          cellRender={(cellData) => {
                            const row = cellData.row.data;
                            const isCanceled = row.status === "canceled";
                            const date = cellData.value
                              ? new Date(cellData.value).toLocaleString()
                              : "-";
                            return (
                              <span
                                style={{
                                  color: isCanceled ? "#757575" : "#E0E6ED",
                                }}
                              >
                                {date}
                              </span>
                            );
                          }}
                        />
                        <Column
                          dataField="confirmedAt"
                          caption="Confirmed"
                          dataType="datetime"
                          format="dd/MM/yyyy HH:mm"
                          minWidth={200}
                          alignment="center"
                          cssClass="date-column"
                          cellRender={(cellData) => {
                            const row = cellData.row.data;
                            const isCanceled = row.status === "canceled";
                            const date = cellData.value
                              ? new Date(cellData.value).toLocaleString()
                              : "-";
                            return (
                              <span
                                style={{
                                  color: isCanceled ? "#757575" : "#E0E6ED",
                                }}
                              >
                                {date}
                              </span>
                            );
                          }}
                        />
                      </DataGrid>
                    </Box>
                    <TablePagination
                      component="div"
                      count={
                        depositsPagination.total + withdrawalsPagination.total
                      }
                      page={
                        Math.max(
                          depositsPagination.page,
                          withdrawalsPagination.page
                        ) - 1
                      }
                      onPageChange={handleWalletHistoryPageChange}
                      rowsPerPage={depositsPagination.limit}
                      onRowsPerPageChange={handleWalletHistoryRowsPerPageChange}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      sx={{
                        backgroundColor: "#2E3B55",
                        color: "#E0E6ED",
                        borderTop: "1px solid #3A4663",
                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                          {
                            color: "#E0E6ED",
                            fontSize: "1.2rem",
                          },
                        "& .MuiTablePagination-select": {
                          color: "#E0E6ED",
                          fontSize: "1.2rem",
                        },
                        "& .MuiTablePagination-actions button": {
                          color: "#E0E6ED",
                        },
                      }}
                    />
                  </>
                )}
                {(initialLoad || isLoading) && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Typography variant="h6" sx={{ color: "#B0BEC5" }}>
                      {initialLoad
                        ? "Loading wallet history..."
                        : "Refreshing..."}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {!initialLoad && !isLoading && Array.isArray(history) && (
                  <>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                      <DataGrid
                        dataSource={history.filter((h) => h && h.transactionId)}
                        keyExpr="transactionId"
                        showBorders={true}
                        showRowLines={true}
                        showColumnLines={true}
                        rowAlternationEnabled={true}
                        className="trader-wallet-grid"
                        height="100%"
                        width="100%"
                        remoteOperations={false}
                        noDataText="No transaction history available"
                      >
                        <Paging enabled={false} />

                        <Column
                          dataField="type"
                          caption="Type"
                          cellRender={renderHistoryTypeCell}
                          minWidth={150}
                          alignment="center"
                        />
                        <Column
                          dataField="amount"
                          caption="Amount"
                          cellRender={renderHistoryAmountCell}
                          minWidth={180}
                          alignment="center"
                        />
                        <Column
                          dataField="commissionRate"
                          caption="Commission Rate"
                          minWidth={150}
                          alignment="center"
                          cellRender={(cellData) => {
                            const rate = cellData.value || 0;
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: "bold",
                                    color: "#FF9800",
                                    fontSize: "1.4rem",
                                  }}
                                >
                                  - {rate.toFixed(2)} %
                                </Typography>
                              </Box>
                            );
                          }}
                        />
                        <Column
                          dataField="commissionFee"
                          caption="Commission Fee"
                          minWidth={180}
                          alignment="center"
                          cellRender={(cellData) => {
                            const fee = cellData.value || 0;
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: "bold",
                                    color: "#FF9800",
                                    fontSize: "1.4rem",
                                  }}
                                >
                                  - {fee.toFixed(2)} AZN
                                </Typography>
                              </Box>
                            );
                          }}
                        />
                        <Column
                          dataField="balanceBefore"
                          caption="Balance Before"
                          minWidth={180}
                          alignment="center"
                          cellRender={(cellData) => {
                            const balance = cellData.value || 0;
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: "600",
                                    color: "#B0BEC5",
                                    fontSize: "1.4rem",
                                  }}
                                >
                                  {balance.toFixed(2)} AZN
                                </Typography>
                              </Box>
                            );
                          }}
                        />
                        <Column
                          dataField="balanceAfter"
                          caption="Balance After"
                          minWidth={180}
                          alignment="center"
                          cellRender={(cellData) => {
                            const balance = cellData.value || 0;
                            return (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: "700",
                                    color: "#4CAF50",
                                    fontSize: "1.5rem",
                                  }}
                                >
                                  {balance.toFixed(2)} AZN
                                </Typography>
                              </Box>
                            );
                          }}
                        />
                        <Column
                          dataField="transactionId"
                          caption="Transaction ID"
                          minWidth={250}
                          alignment="center"
                        />
                      </DataGrid>
                    </Box>
                    <TablePagination
                      component="div"
                      count={historyPagination.total}
                      page={historyPagination.page - 1}
                      onPageChange={handleTransactionHistoryPageChange}
                      rowsPerPage={historyPagination.limit}
                      onRowsPerPageChange={
                        handleTransactionHistoryRowsPerPageChange
                      }
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      sx={{
                        backgroundColor: "#2E3B55",
                        color: "#E0E6ED",
                        borderTop: "1px solid #3A4663",
                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                          {
                            color: "#E0E6ED",
                            fontSize: "1.2rem",
                          },
                        "& .MuiTablePagination-select": {
                          color: "#E0E6ED",
                          fontSize: "1.2rem",
                        },
                        "& .MuiTablePagination-actions button": {
                          color: "#E0E6ED",
                        },
                      }}
                    />
                  </>
                )}
                {(initialLoad || isLoading) && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Typography variant="h6" sx={{ color: "#B0BEC5" }}>
                      {initialLoad ? "Loading history..." : "Refreshing..."}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Deposit Popup */}
      <Popup
        width={1000}
        height={500}
        showTitle={false}
        dragEnabled={false}
        hideOnOutsideClick={true}
        visible={depositPopupVisible}
        onHiding={() => setDepositPopupVisible(false)}
        contentRender={renderDepositPopup}
        showCloseButton={true}
        container=".dx-viewport"
        animation={{
          show: { type: "slide", duration: 400, from: { top: -50 } },
        }}
        position={{ my: "center", at: "center", of: window }}
      />

      {/* Withdrawal Popup */}
      <Popup
        width={900}
        height={650}
        showTitle={false}
        dragEnabled={false}
        hideOnOutsideClick={true}
        visible={withdrawalPopupVisible}
        onHiding={() => setWithdrawalPopupVisible(false)}
        contentRender={renderWithdrawalPopup}
        showCloseButton={true}
        container=".dx-viewport"
        animation={{
          show: { type: "slide", duration: 400, from: { top: -50 } },
        }}
        position={{ my: "center", at: "center", of: window }}
      />
    </div>
  );
};

export default TraderWalletView;
